import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Settings as SettingsIcon, 
  Upload, 
  Download, 
  Link2, 
  Unlink,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Bell,
  Mail,
  Clock,
  Send,
  Users,
  Plus,
  Trash2,
  Play,
  Pause,
  Calendar,
  Shield,
  History,
  TrendingUp,
  Crown,
  Edit,
  Lock,
  Eye,
  EyeOff,
  User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { qboApi, importApi, notificationsApi, authApi, adminApi, projectsApi, dealFlowApi, investorApi } from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { appSettingsApi } from '../lib/api';

export const Settings = () => {
  const { isSuperOwner, isOwner, user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [qbStatus, setQbStatus] = useState({ connected: false, loading: true });
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [importType, setImportType] = useState(null);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Notification state
  const [notifSettings, setNotifSettings] = useState(null);
  const [notifLoading, setNotifLoading] = useState(true);
  const [agingData, setAgingData] = useState(null);
  const [newRecipient, setNewRecipient] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [togglingScheduler, setTogglingScheduler] = useState(false);
  const [notifLogs, setNotifLogs] = useState([]);
  
  // Digest report config state
  const [digestConfig, setDigestConfig] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [sendingDigestReport, setSendingDigestReport] = useState(false);
  const [newDigestRecipient, setNewDigestRecipient] = useState('');
  const [calendlyUrl, setCalendlyUrl] = useState('');
  
  // Access control state
  const [authorizedEmails, setAuthorizedEmails] = useState([]);
  const [newAuthorizedEmail, setNewAuthorizedEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('owner');
  const [newInvestorName, setNewInvestorName] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [changingRole, setChangingRole] = useState(null);
  
  // Role change handler
  const handleRoleChange = async (email, newRole) => {
    setChangingRole(email);
    try {
      await authApi.updateAuthorizedEmail(email, { role: newRole });
      toast.success(`Updated ${email} to ${newRole.replace('_', ' ')}`);
      loadAuthorizedEmails();
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setChangingRole(null);
    }
  };

  useEffect(() => {
    // Check if returning from QuickBooks OAuth
    if (searchParams.get('qbo') === 'connected') {
      toast.success('QuickBooks connected successfully!');
    }
    
    // Only load owner-specific settings
    if (isOwner) {
      loadQBStatus();
      loadNotificationSettings();
      loadAgingData();
      loadSchedulerStatus();
      loadNotificationLogs();
      loadAuthorizedEmails();
      loadDigestConfig();
      loadAppSettings();
    }
    // Loaders are defined inline in the component and only read constant
    // dependencies (authApi/api modules + state setters). They're stable
    // across renders, so excluding them from deps avoids both stale-closure
    // bugs AND the re-render loop that would happen if we added them
    // without useCallback wrapping. Intentional disable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isOwner]);

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    try {
      await authApi.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to change password';
      toast.error(detail);
    } finally {
      setChangingPassword(false);
    }
  };

  const loadAuthorizedEmails = async () => {
    try {
      const response = await authApi.getAuthorizedEmails();
      setAuthorizedEmails(response.data);
    } catch (error) {
      console.error('Failed to load authorized emails:', error);
    }
  };

  const handleAddAuthorizedEmail = async () => {
    if (!newAuthorizedEmail || !newAuthorizedEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (newUserRole === 'investor' && !newInvestorName.trim()) {
      toast.error('Investor name is required for investor accounts');
      return;
    }
    
    setAddingEmail(true);
    try {
      await authApi.addAuthorizedEmail({
        email: newAuthorizedEmail,
        role: newUserRole,
        investor_name: newUserRole === 'investor' ? newInvestorName.trim() : null
      });
      toast.success(`${newAuthorizedEmail} has been authorized as ${newUserRole}`);
      setNewAuthorizedEmail('');
      setNewUserRole('owner');
      setNewInvestorName('');
      loadAuthorizedEmails();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to add email';
      toast.error(msg);
    } finally {
      setAddingEmail(false);
    }
  };

  const handleUpdateUserRole = async (email, role, investorName) => {
    try {
      await authApi.updateAuthorizedEmail(email, {
        role: role,
        investor_name: role === 'investor' ? investorName : null
      });
      toast.success(`Updated ${email} to ${role}`);
      setEditingUser(null);
      loadAuthorizedEmails();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update user';
      toast.error(msg);
    }
  };

  const handleRemoveAuthorizedEmail = async (email) => {
    try {
      await authApi.removeAuthorizedEmail(email);
      toast.success(`${email} has been removed`);
      loadAuthorizedEmails();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to remove email';
      toast.error(msg);
    }
  };

  const [sendingResetTo, setSendingResetTo] = useState(null);
  const [sendingResetAll, setSendingResetAll] = useState(false);

  const handleViewInvestorPortal = async (email) => {
    // Start admin impersonation: store target email so the axios interceptor
    // sends X-Impersonate-Email on every API call. Refresh AuthContext so the
    // sidebar/role swap to the investor's view, then navigate to /investor.
    try {
      localStorage.setItem('impersonate_email', email);
      await checkAuth(true);
      toast.success(`Now viewing as ${email}`);
      navigate('/investor');
    } catch (err) {
      localStorage.removeItem('impersonate_email');
      toast.error('Failed to start impersonation');
    }
  };

  const handleSendResetLink = async (email) => {
    setSendingResetTo(email);
    try {
      await authApi.sendResetLink(email);
      toast.success(`Reset link sent to ${email}`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send reset link';
      toast.error(msg);
    } finally {
      setSendingResetTo(null);
    }
  };

  const handleSendResetAll = async () => {
    if (!window.confirm('Send password reset links to ALL users (except yourself)?')) return;
    setSendingResetAll(true);
    try {
      const res = await authApi.sendResetLinkAll();
      toast.success(res.data.message);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send reset links';
      toast.error(msg);
    } finally {
      setSendingResetAll(false);
    }
  };

  const loadQBStatus = async () => {
    try {
      const response = await qboApi.getStatus();
      setQbStatus({ ...response.data, loading: false });
      // If connected, load classes and mappings
      if (response.data.connected) {
        // Class management is now on the QBO Sync page
      }
    } catch (error) {
      console.error('Failed to load QB status:', error);
      setQbStatus({ connected: false, loading: false });
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const response = await notificationsApi.getSchedulerStatus();
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Failed to load scheduler status:', error);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const response = await notificationsApi.getSettings();
      setNotifSettings(response.data);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    } finally {
      setNotifLoading(false);
    }
  };

  const loadAgingData = async () => {
    try {
      const response = await notificationsApi.checkAging();
      setAgingData(response.data);
    } catch (error) {
      console.error('Failed to load aging data:', error);
    }
  };

  const loadNotificationLogs = async () => {
    try {
      const response = await notificationsApi.getLogs(20);
      setNotifLogs(response.data);
    } catch (error) {
      console.error('Failed to load notification logs:', error);
    }
  };

  const loadDigestConfig = async () => {
    try {
      const response = await notificationsApi.getDigestConfig();
      setDigestConfig(response.data);
    } catch (error) {
      console.error('Failed to load digest config:', error);
    }
  };

  const updateDigestSetting = async (key, value) => {
    const updated = { ...digestConfig, [key]: value };
    setDigestConfig(updated);
    try {
      await notificationsApi.updateDigestConfig({ [key]: value });
    } catch (error) {
      toast.error('Failed to save digest setting');
      loadDigestConfig();
    }
  };

  const updateDigestDataPoint = async (key, value) => {
    const newPoints = { ...(digestConfig?.data_points || {}), [key]: value };
    setDigestConfig(prev => ({ ...prev, data_points: newPoints }));
    try {
      await notificationsApi.updateDigestConfig({ data_points: newPoints });
    } catch (error) {
      toast.error('Failed to save digest setting');
      loadDigestConfig();
    }
  };

  const addDigestRecipient = async () => {
    if (!newDigestRecipient || !newDigestRecipient.includes('@')) return;
    const recipients = [...(digestConfig?.recipients || [])];
    if (recipients.includes(newDigestRecipient)) {
      toast.error('Recipient already added');
      return;
    }
    recipients.push(newDigestRecipient);
    setDigestConfig(prev => ({ ...prev, recipients }));
    setNewDigestRecipient('');
    try {
      await notificationsApi.updateDigestConfig({ recipients });
    } catch (error) {
      toast.error('Failed to add recipient');
      loadDigestConfig();
    }
  };

  const removeDigestRecipient = async (email) => {
    const recipients = (digestConfig?.recipients || []).filter(e => e !== email);
    setDigestConfig(prev => ({ ...prev, recipients }));
    try {
      await notificationsApi.updateDigestConfig({ recipients });
    } catch (error) {
      toast.error('Failed to remove recipient');
      loadDigestConfig();
    }
  };

  const handleSendDigestReport = async () => {
    setSendingDigestReport(true);
    try {
      const res = await notificationsApi.sendDigestReport();
      toast.success(res.data.message || 'Digest report sent');
      loadDigestConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send digest report');
    } finally {
      setSendingDigestReport(false);
    }
  };

  const loadAppSettings = async () => {
    try {
      const res = await appSettingsApi.get();
      setCalendlyUrl(res.data?.calendly_url || '');
    } catch (error) {
      console.error('Failed to load app settings:', error);
    }
  };

  const saveCalendlyUrl = async (url) => {
    setCalendlyUrl(url);
    try {
      await appSettingsApi.update({ calendly_url: url });
    } catch (error) {
      toast.error('Failed to save Calendly URL');
    }
  };

  const handleFileSelect = (type) => {
    setImportType(type);
    fileInputRef.current?.click();
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const data = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      data.push(row);
    }
    
    return data;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResults(null);

    try {
      const text = await file.text();
      const data = parseCSV(text);
      
      if (data.length === 0) {
        toast.error('No data found in CSV file');
        setImporting(false);
        return;
      }

      let response;
      switch (importType) {
        case 'projects':
          response = await importApi.projects(data);
          break;
        case 'transactions':
          response = await importApi.transactions(data);
          break;
        case 'jv-agreements':
          response = await importApi.jvAgreements(data);
          break;
        default:
          throw new Error('Unknown import type');
      }

      setImportResults(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`Imported ${response.data.success_count} records`);
      }
      if (response.data.error_count > 0) {
        toast.error(`${response.data.error_count} records failed`);
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import CSV file');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const downloadTemplate = (type) => {
    let csv = '';
    switch (type) {
      case 'projects':
        csv = 'Name,Status,Strategy,Purchase Price,Sale Price,Acquisition Date,Sale Date,Acres,County,State,Projected Profit,Notes,Terranotes ID\n';
        csv += 'Example Ranch,Lead,Flip,50000,0,2025-01-15,,10.5,Travis,TX,25000,Test property,TN-001';
        break;
      case 'transactions':
        csv = 'Project Name,Type,Category,Amount,Description,Date\n';
        csv += 'Example Ranch,Expense,Due Diligence,500,Title search,2025-01-20';
        break;
      case 'jv-agreements':
        csv = 'Project Name,Investor Name,Capital Contributed,Tier 1 Days,Tier 1 Split,Tier 2 Days,Tier 2 Split,Tier 3 Days,Tier 3 Split,Tier 4 Days,Tier 4 Split,Tier 5 Split\n';
        csv += 'Example Ranch,John Smith,100000,180,0.40,270,0.45,365,0.50,456,0.60,0.65';
        break;
      default:
        return;
    }
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const updateNotifSetting = async (key, value) => {
    try {
      const update = { [key]: value };
      await notificationsApi.updateSettings(update);
      setNotifSettings(prev => ({ ...prev, ...update }));
      toast.success('Settings updated');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    }
  };

  const addRecipient = async () => {
    if (!newRecipient || !newRecipient.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    const recipients = [...(notifSettings?.default_recipients || []), newRecipient];
    await updateNotifSetting('default_recipients', recipients);
    setNewRecipient('');
  };

  const removeRecipient = async (email) => {
    const recipients = (notifSettings?.default_recipients || []).filter(r => r !== email);
    await updateNotifSetting('default_recipients', recipients);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSendingTest(true);
    try {
      await notificationsApi.sendTestEmail(testEmail);
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const response = await notificationsApi.sendDigest();
      if (response.data.sent > 0) {
        toast.success(`Digest sent to ${response.data.sent} recipients`);
      } else {
        toast.warning(response.data.message || 'No emails sent');
      }
    } catch (error) {
      console.error('Failed to send digest:', error);
      toast.error('Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const toggleScheduler = async () => {
    setTogglingScheduler(true);
    try {
      if (schedulerStatus?.enabled) {
        await notificationsApi.stopScheduler();
        toast.success('Scheduler stopped');
      } else {
        await notificationsApi.startScheduler();
        toast.success('Scheduler started');
      }
      await loadSchedulerStatus();
    } catch (error) {
      console.error('Failed to toggle scheduler:', error);
      toast.error('Failed to toggle scheduler');
    } finally {
      setTogglingScheduler(false);
    }
  };

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-bold flex items-center gap-2">
          <SettingsIcon className="w-5 h-5 sm:w-6 sm:h-6" />
          Settings
        </h1>
        <p className="text-xs sm:text-sm text-muted-foreground">Manage integrations, notifications, and import data</p>
      </div>

      <Tabs defaultValue="account" className="space-y-4">
        <TabsList className="bg-muted/50 rounded-sm overflow-x-auto flex justify-start w-full">
          <TabsTrigger value="account" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap" data-testid="tab-account">
            <User className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Account</span><span className="sm:hidden">Account</span>
          </TabsTrigger>
          {isOwner && (
            <TabsTrigger value="notifications" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">
              <Bell className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Notifications</span><span className="sm:hidden">Alerts</span>
            </TabsTrigger>
          )}
          {isOwner && (
            <TabsTrigger value="access" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">
              <Shield className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Access Control</span><span className="sm:hidden">Access</span>
            </TabsTrigger>
          )}
          {isOwner && (
            <TabsTrigger value="integrations" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">
              <Link2 className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Integrations</span><span className="sm:hidden">Integrate</span>
            </TabsTrigger>
          )}
          {isOwner && (
            <TabsTrigger value="import" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm whitespace-nowrap">
              <Upload className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Data Import</span><span className="sm:hidden">Import</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Account Tab - Visible to ALL users */}
        <TabsContent value="account">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Account Info */}
            <Card className="rounded-sm border" data-testid="account-info-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-5 h-5 text-forest-500" />
                  Account Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-4 bg-muted/30 rounded-sm space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Email</span>
                    <span className="font-medium">{user?.email}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Name</span>
                    <span className="font-medium">{user?.name || user?.investor_name || '-'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Role</span>
                    <Badge variant="outline" className="rounded-sm text-xs capitalize">
                      {(user?.role || '').replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Change Password */}
            <Card className="rounded-sm border" data-testid="change-password-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-500" />
                  Change Password
                </CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="rounded-sm pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="At least 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="rounded-sm pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                  <Input
                    id="confirmNewPassword"
                    type="password"
                    placeholder="Re-enter new password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="rounded-sm"
                    data-testid="confirm-new-password-input"
                  />
                </div>
                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword || !currentPassword || !newPassword || !confirmNewPassword}
                  className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
                  data-testid="change-password-btn"
                >
                  {changingPassword ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Changing...</>
                  ) : (
                    <><Lock className="w-4 h-4 mr-2" /> Change Password</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Aging Status */}
            <Card className="rounded-sm border" data-testid="aging-status-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Current Aging Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                {agingData ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-red-50 rounded-sm text-center">
                        <div className="text-3xl font-bold text-red-600">{agingData.critical_count}</div>
                        <div className="text-xs text-red-700">CRITICAL</div>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-sm text-center">
                        <div className="text-3xl font-bold text-amber-600">{agingData.warning_count}</div>
                        <div className="text-xs text-amber-700">WARNING</div>
                      </div>
                    </div>
                    <Button 
                      onClick={handleSendDigest}
                      disabled={sendingDigest || !(notifSettings?.default_recipients?.length > 0)}
                      className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm"
                      data-testid="send-digest-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {sendingDigest ? 'Sending...' : 'Send Digest Now'}
                    </Button>
                    {!(notifSettings?.default_recipients?.length > 0) && (
                      <p className="text-xs text-muted-foreground text-center">Add recipients below to enable</p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-24">
                    <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Test Email */}
            <Card className="rounded-sm border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-500" />
                  Test Email
                </CardTitle>
                <CardDescription>Verify your email configuration</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="rounded-sm"
                  />
                  <Button
                    onClick={handleSendTestEmail}
                    disabled={sendingTest}
                    className="rounded-sm"
                    data-testid="send-test-email-btn"
                  >
                    {sendingTest ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Thresholds */}
            <Card className="rounded-sm border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-500" />
                  Aging Thresholds
                </CardTitle>
                <CardDescription>Customize when alerts are triggered</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                    Warning Threshold (days)
                  </Label>
                  <Input
                    type="number"
                    value={notifSettings?.warning_threshold_days || 200}
                    onChange={(e) => updateNotifSetting('warning_threshold_days', parseInt(e.target.value))}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    Critical Threshold (days)
                  </Label>
                  <Input
                    type="number"
                    value={notifSettings?.critical_threshold_days || 300}
                    onChange={(e) => updateNotifSetting('critical_threshold_days', parseInt(e.target.value))}
                    className="rounded-sm"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Notification Toggles */}
            <Card className="rounded-sm border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell className="w-5 h-5 text-green-500" />
                  Notification Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Daily Digest</p>
                    <p className="text-xs text-muted-foreground">Summary of all aging properties</p>
                  </div>
                  <Switch
                    checked={notifSettings?.daily_digest_enabled ?? true}
                    onCheckedChange={(checked) => updateNotifSetting('daily_digest_enabled', checked)}
                    data-testid="daily-digest-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Immediate Critical Alerts</p>
                    <p className="text-xs text-muted-foreground">Alert when project hits critical threshold</p>
                  </div>
                  <Switch
                    checked={notifSettings?.immediate_critical_alerts ?? true}
                    onCheckedChange={(checked) => updateNotifSetting('immediate_critical_alerts', checked)}
                    data-testid="immediate-alerts-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Recipients */}
            <Card className="rounded-sm border lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-500" />
                  Default Recipients
                </CardTitle>
                <CardDescription>Who should receive aging notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Add recipient email..."
                    value={newRecipient}
                    onChange={(e) => setNewRecipient(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addRecipient()}
                    className="rounded-sm"
                  />
                  <Button onClick={addRecipient} className="rounded-sm" data-testid="add-recipient-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(notifSettings?.default_recipients || []).map((email) => (
                    <Badge key={email} variant="secondary" className="rounded-sm flex items-center gap-1 py-1 px-2">
                      <Mail className="w-3 h-3" />
                      {email}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="ml-1 hover:text-red-600">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-sm">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove Recipient?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Remove <strong>{email}</strong> from default notification recipients? 
                              They will no longer receive automatic investor notifications.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => removeRecipient(email)}
                              className="bg-red-600 hover:bg-red-700 rounded-sm"
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </Badge>
                  ))}
                  {(notifSettings?.default_recipients || []).length === 0 && (
                    <p className="text-sm text-muted-foreground">No recipients configured yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Scheduler */}
            <Card className="rounded-sm border lg:col-span-2" data-testid="scheduler-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-teal-500" />
                      Automated Digest Scheduler
                    </CardTitle>
                    <CardDescription>Automatically send digests on a schedule</CardDescription>
                  </div>
                  {schedulerStatus?.enabled ? (
                    <Badge className="bg-green-100 text-green-700 rounded-sm">Running</Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-sm">Stopped</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Frequency */}
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select
                      value={notifSettings?.schedule_frequency || 'daily'}
                      onValueChange={(value) => updateNotifSetting('schedule_frequency', value)}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Time */}
                  <div className="space-y-2">
                    <Label>Time (24hr)</Label>
                    <Input
                      type="time"
                      value={notifSettings?.digest_time || '08:00'}
                      onChange={(e) => updateNotifSetting('digest_time', e.target.value)}
                      className="rounded-sm"
                    />
                  </div>

                  {/* Day of Week (for weekly/biweekly) */}
                  {(notifSettings?.schedule_frequency === 'weekly' || notifSettings?.schedule_frequency === 'biweekly') && (
                    <div className="space-y-2">
                      <Label>Day of Week</Label>
                      <Select
                        value={String(notifSettings?.schedule_day_of_week || 0)}
                        onValueChange={(value) => updateNotifSetting('schedule_day_of_week', parseInt(value))}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {dayNames.map((day, i) => (
                            <SelectItem key={day} value={String(i)}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Day of Month (for monthly) */}
                  {notifSettings?.schedule_frequency === 'monthly' && (
                    <div className="space-y-2">
                      <Label>Day of Month</Label>
                      <Select
                        value={String(notifSettings?.schedule_day_of_month || 1)}
                        onValueChange={(value) => updateNotifSetting('schedule_day_of_month', parseInt(value))}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({length: 28}, (_, i) => i + 1).map((day) => (
                            <SelectItem key={day} value={String(day)}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Last sent info */}
                {schedulerStatus?.last_sent && (
                  <p className="text-xs text-muted-foreground">
                    Last sent: {new Date(schedulerStatus.last_sent).toLocaleString()}
                  </p>
                )}

                {/* Start/Stop button */}
                <Button
                  onClick={toggleScheduler}
                  disabled={togglingScheduler || !(notifSettings?.default_recipients?.length > 0)}
                  variant={schedulerStatus?.enabled ? "outline" : "default"}
                  className={`rounded-sm ${!schedulerStatus?.enabled ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}
                  data-testid="toggle-scheduler-btn"
                >
                  {togglingScheduler ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : schedulerStatus?.enabled ? (
                    <Pause className="w-4 h-4 mr-2" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  {schedulerStatus?.enabled ? 'Stop Scheduler' : 'Start Scheduler'}
                </Button>
                {!(notifSettings?.default_recipients?.length > 0) && (
                  <p className="text-xs text-muted-foreground">Add recipients above to enable scheduler</p>
                )}
              </CardContent>
            </Card>

            {/* Notification History */}
            <Card className="rounded-sm border lg:col-span-2" data-testid="notification-history-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="w-5 h-5 text-gray-500" />
                      Notification History
                    </CardTitle>
                    <CardDescription>Recent emails sent from the system</CardDescription>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={loadNotificationLogs}
                    className="rounded-sm"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {notifLogs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No notifications sent yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {notifLogs.map((log) => (
                      <div 
                        key={log.id} 
                        className="flex items-start gap-3 p-3 bg-muted/30 rounded-sm border"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          log.type === 'scheduled_digest' ? 'bg-teal-100' :
                          log.type === 'daily_digest' ? 'bg-blue-100' :
                          log.type === 'immediate_alert' ? 'bg-red-100' : 'bg-gray-100'
                        }`}>
                          {log.type === 'scheduled_digest' ? (
                            <Calendar className="w-4 h-4 text-teal-600" />
                          ) : log.type === 'immediate_alert' ? (
                            <AlertCircle className="w-4 h-4 text-red-600" />
                          ) : (
                            <Mail className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm capitalize">
                              {log.type?.replace(/_/g, ' ') || 'Notification'}
                            </span>
                            {log.frequency && (
                              <Badge variant="outline" className="rounded-sm text-xs">
                                {log.frequency}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(log.sent_at).toLocaleString()}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {log.recipients?.map((r, i) => (
                              <span key={`${log.id}-${r}-${i}`} className="text-xs bg-muted px-2 py-0.5 rounded">
                                {r}
                              </span>
                            ))}
                          </div>
                          {log.results && (
                            <div className="flex items-center gap-3 mt-2 text-xs">
                              <span className="flex items-center gap-1 text-green-600">
                                <CheckCircle className="w-3 h-3" />
                                {log.results.filter(r => r.success).length} sent
                              </span>
                              {log.results.filter(r => !r.success).length > 0 && (
                                <span className="flex items-center gap-1 text-red-600">
                                  <XCircle className="w-3 h-3" />
                                  {log.results.filter(r => !r.success).length} failed
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Email Digest Report Configuration */}
          <Card className="rounded-sm border mt-6" data-testid="digest-report-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-500" />
                  Email Digest Report
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Label htmlFor="digest-enabled" className="text-xs text-muted-foreground">Enabled</Label>
                  <Switch
                    id="digest-enabled"
                    checked={digestConfig?.enabled || false}
                    onCheckedChange={(v) => updateDigestSetting('enabled', v)}
                    data-testid="digest-enabled-toggle"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Schedule automated email reports with portfolio metrics and financial summaries
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Frequency */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">Frequency</Label>
                  <Select
                    value={digestConfig?.frequency || 'weekly'}
                    onValueChange={(v) => updateDigestSetting('frequency', v)}
                  >
                    <SelectTrigger className="h-9 rounded-sm" data-testid="digest-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {digestConfig?.frequency === 'weekly' && (
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">Day of Week</Label>
                    <Select
                      value={String(digestConfig?.day_of_week ?? 1)}
                      onValueChange={(v) => updateDigestSetting('day_of_week', parseInt(v))}
                    >
                      <SelectTrigger className="h-9 rounded-sm" data-testid="digest-day-of-week">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d, i) => (
                          <SelectItem key={d} value={String(i)}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {digestConfig?.frequency === 'monthly' && (
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">Day of Month</Label>
                    <Select
                      value={String(digestConfig?.day_of_month ?? 1)}
                      onValueChange={(v) => updateDigestSetting('day_of_month', parseInt(v))}
                    >
                      <SelectTrigger className="h-9 rounded-sm" data-testid="digest-day-of-month">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,5,10,15,20,25].map(d => (
                          <SelectItem key={d} value={String(d)}>{d}{d===1?'st':'th'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">Send Time</Label>
                  <Select
                    value={digestConfig?.send_time || '08:00'}
                    onValueChange={(v) => updateDigestSetting('send_time', v)}
                  >
                    <SelectTrigger className="h-9 rounded-sm" data-testid="digest-send-time">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['06:00','07:00','08:00','09:00','10:00','12:00','14:00','16:00','18:00'].map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              {/* Data Points */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 block">Include in Report</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { key: 'portfolio_summary', label: 'Portfolio Summary' },
                    { key: 'pipeline_status', label: 'Active Pipeline' },
                    { key: 'aging_alerts', label: 'Aging Alerts' },
                    { key: 'recent_transactions', label: 'Recent Transactions' },
                    { key: 'pnl_summary', label: 'P&L Summary' },
                    { key: 'capital_position', label: 'Capital Position' },
                  ].map(dp => (
                    <div key={dp.key} className="flex items-center gap-2">
                      <Checkbox
                        id={`dp-${dp.key}`}
                        checked={digestConfig?.data_points?.[dp.key] ?? false}
                        onCheckedChange={(v) => updateDigestDataPoint(dp.key, !!v)}
                        data-testid={`digest-dp-${dp.key}`}
                      />
                      <Label htmlFor={`dp-${dp.key}`} className="text-sm cursor-pointer">{dp.label}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Recipients */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 block">Recipients</Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={newDigestRecipient}
                    onChange={(e) => setNewDigestRecipient(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addDigestRecipient()}
                    className="h-9 rounded-sm flex-1"
                    data-testid="digest-new-recipient"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 rounded-sm"
                    onClick={addDigestRecipient}
                    disabled={!newDigestRecipient}
                    data-testid="digest-add-recipient"
                  >
                    Add
                  </Button>
                </div>
                {(digestConfig?.recipients || []).length > 0 ? (
                  <div className="space-y-1.5">
                    {digestConfig.recipients.map(email => (
                      <div key={email} className="flex items-center justify-between p-2 bg-muted/30 rounded-sm">
                        <span className="text-sm">{email}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDigestRecipient(email)}
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-red-600"
                          data-testid={`digest-remove-${email}`}
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No recipients added yet</p>
                )}
              </div>

              <Separator />

              {/* Send Now / Last Sent */}
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  {digestConfig?.last_sent
                    ? `Last sent: ${new Date(digestConfig.last_sent).toLocaleString()}`
                    : 'Never sent'}
                </div>
                <Button
                  onClick={handleSendDigestReport}
                  disabled={sendingDigestReport || !(digestConfig?.recipients?.length > 0)}
                  className="bg-[#1A2F23] hover:bg-[#1A2F23]/90 text-white rounded-sm gap-1.5"
                  data-testid="send-digest-report-btn"
                >
                  <Send className="w-3.5 h-3.5" />
                  {sendingDigestReport ? 'Sending...' : 'Send Now'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Calendly URL Config */}
          <Card className="rounded-sm border mt-6" data-testid="calendly-config-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Calendly Integration
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Set your Calendly URL for partner briefing scheduling links in approval emails
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  value={calendlyUrl}
                  onChange={(e) => setCalendlyUrl(e.target.value)}
                  placeholder="https://calendly.com/your-link"
                  className="h-9 rounded-sm flex-1"
                  data-testid="calendly-url-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 rounded-sm"
                  onClick={() => saveCalendlyUrl(calendlyUrl)}
                  data-testid="save-calendly-btn"
                >
                  Save
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Access Control Tab */}
        <TabsContent value="access">
          <div className="space-y-6">
            <Card className="rounded-sm border" data-testid="authorized-emails-card">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      Authorized Users
                    </CardTitle>
                    <CardDescription>
                      Manage user access and roles. Super-Owners have full access, Owners have limited permissions, and Investors can only view their JV projects.
                    </CardDescription>
                  </div>
                  {isSuperOwner && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm gap-1.5"
                      onClick={handleSendResetAll}
                      disabled={sendingResetAll}
                      data-testid="send-reset-all-btn"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {sendingResetAll ? 'Sending...' : 'Send Reset Link to All'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add new user - Super-Owner only */}
                {isSuperOwner && <div className="p-4 bg-muted/30 rounded-sm border space-y-3">
                  <Label className="text-sm font-medium">Add New User</Label>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <Input
                        type="email"
                        placeholder="Email address..."
                        value={newAuthorizedEmail}
                        onChange={(e) => setNewAuthorizedEmail(e.target.value)}
                        className="rounded-sm"
                        data-testid="new-user-email-input"
                      />
                    </div>
                    <div>
                      <Select value={newUserRole} onValueChange={setNewUserRole}>
                        <SelectTrigger className="rounded-sm" data-testid="new-user-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {isSuperOwner && (
                            <SelectItem value="super_owner">
                              <span className="flex items-center gap-1">
                                <Crown className="w-3 h-3 text-amber-500" /> Super-Owner
                              </span>
                            </SelectItem>
                          )}
                          <SelectItem value="owner">Owner</SelectItem>
                          <SelectItem value="investor">Investor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Button 
                        onClick={handleAddAuthorizedEmail}
                        disabled={addingEmail}
                        className="w-full rounded-sm bg-blue-600 hover:bg-blue-700"
                        data-testid="add-authorized-email-btn"
                      >
                        {addingEmail ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                        Add User
                      </Button>
                    </div>
                  </div>
                  {newUserRole === 'investor' && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Investor Name (must match JV Agreement)</Label>
                      <Input
                        type="text"
                        placeholder="e.g., John Smith"
                        value={newInvestorName}
                        onChange={(e) => setNewInvestorName(e.target.value)}
                        className="rounded-sm mt-1"
                        data-testid="new-user-investor-name-input"
                      />
                    </div>
                  )}
                </div>}
                
                {/* List of authorized users */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Current Users ({authorizedEmails.length})</Label>
                  {authorizedEmails.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      No authorized users configured.
                    </p>
                  ) : (
                    authorizedEmails.map((item) => {
                      const isCurrentUser = user?.email?.toLowerCase() === item.email?.toLowerCase();
                      const getRoleIcon = (role) => {
                        if (role === 'super_owner') return <Crown className="w-4 h-4 text-amber-500" />;
                        if (role === 'investor') return <TrendingUp className="w-4 h-4 text-purple-600" />;
                        return <Shield className="w-4 h-4 text-blue-600" />;
                      };
                      const getRoleBadgeClass = (role) => {
                        if (role === 'super_owner') return 'bg-amber-50 text-amber-700 border-amber-200';
                        if (role === 'investor') return 'bg-purple-50 text-purple-700 border-purple-200';
                        return 'bg-blue-50 text-blue-700 border-blue-200';
                      };
                      const getRoleLabel = (role) => {
                        if (role === 'super_owner') return 'Super-Owner';
                        if (role === 'investor') return 'Investor';
                        return 'Owner';
                      };
                      
                      return (
                        <div 
                          key={item.id || item.email} 
                          className={`flex items-center justify-between p-3 bg-muted/30 rounded-sm border ${isCurrentUser ? 'ring-2 ring-forest-500/30' : ''}`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              item.role === 'super_owner' ? 'bg-amber-100' :
                              item.role === 'investor' ? 'bg-purple-100' : 'bg-blue-100'
                            }`}>
                              {getRoleIcon(item.role)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{item.email}</p>
                                {isCurrentUser && (
                                  <Badge variant="outline" className="rounded-sm text-xs bg-forest-50 text-forest-700 border-forest-200">
                                    You
                                  </Badge>
                                )}
                                {!isSuperOwner && (
                                  <Badge 
                                    variant="outline" 
                                    className={`rounded-sm text-xs ${getRoleBadgeClass(item.role)}`}
                                  >
                                    {getRoleLabel(item.role)}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {item.investor_name && <span className="text-purple-600">Investor: {item.investor_name} • </span>}
                                Added by {item.added_by || 'system'} {item.added_at && `• ${formatDate(item.added_at)}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Role selector - only for super_owner, not for self */}
                            {isSuperOwner && !isCurrentUser && (
                              <Select 
                                value={item.role || 'owner'} 
                                onValueChange={(newRole) => handleRoleChange(item.email, newRole)}
                                disabled={changingRole === item.email}
                              >
                                <SelectTrigger 
                                  className="w-[140px] h-8 rounded-sm text-xs" 
                                  data-testid={`role-select-${item.email}`}
                                >
                                  {changingRole === item.email ? (
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <SelectValue />
                                  )}
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="super_owner">
                                    <span className="flex items-center gap-1">
                                      <Crown className="w-3 h-3 text-amber-500" /> Super-Owner
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="owner">
                                    <span className="flex items-center gap-1">
                                      <Shield className="w-3 h-3 text-blue-500" /> Owner
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="investor">
                                    <span className="flex items-center gap-1">
                                      <TrendingUp className="w-3 h-3 text-purple-500" /> Investor
                                    </span>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            {/* View Investor Portal — always available; backend returns
                                a graceful empty state when intake isn't completed yet. */}
                            {isSuperOwner && item.role === 'investor' && !isCurrentUser && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                onClick={() => handleViewInvestorPortal(item.email)}
                                data-testid={`view-portal-${item.email}`}
                                title={item.has_partner_record ? "View investor portal" : "View investor portal (intake not completed)"}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            {/* Send Reset Link - for other users */}
                            {isSuperOwner && !isCurrentUser && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => handleSendResetLink(item.email)}
                                disabled={sendingResetTo === item.email}
                                data-testid={`send-reset-${item.email}`}
                                title="Send password reset link"
                              >
                                {sendingResetTo === item.email ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            {/* Delete button - Super-Owner only, not for self */}
                            {isSuperOwner && !isCurrentUser && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="rounded-sm">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Remove Access?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will prevent <strong>{item.email}</strong> from signing into the application.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => handleRemoveAuthorizedEmail(item.email)}
                                      className="bg-red-600 hover:bg-red-700 rounded-sm"
                                    >
                                      Remove Access
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

 

        {/* Integrations Tab */}
        <TabsContent value="integrations">
          <div className="space-y-4">
            {/* QuickBooks Card - Links to QBO Sync */}
            <Card className="rounded-sm border" data-testid="quickbooks-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-sm flex items-center justify-center">
                      <span className="text-green-700 font-bold text-lg">QB</span>
                    </div>
                    <div>
                      <CardTitle className="text-lg">QuickBooks Online</CardTitle>
                      <CardDescription>Sync transactions with QuickBooks</CardDescription>
                    </div>
                  </div>
                  {qbStatus.loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                  ) : qbStatus.connected ? (
                    <Badge className="bg-green-100 text-green-700 rounded-sm">Connected</Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-sm">Not Connected</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {qbStatus.connected && (
                    <div className="p-3 bg-muted/30 rounded-sm text-sm">
                      <p className="font-medium">{qbStatus.company_name || 'Connected Company'}</p>
                      {qbStatus.token_error && (
                        <p className="text-amber-600 text-xs mt-1">Connection issue: {qbStatus.token_error}</p>
                      )}
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Manage your QuickBooks connection, class mappings, and sync settings from the QBO Sync page.
                  </p>
                  <Link to="/qbo-sync">
                    <Button className="w-full rounded-sm gap-2">
                      <Link2 className="w-4 h-4" /> {qbStatus.connected ? 'Manage QBO Sync' : 'Connect QuickBooks'}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Other Integration Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="rounded-sm border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-sm flex items-center justify-center">
                      <Mail className="w-6 h-6 text-purple-700" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Email (Resend)</CardTitle>
                      <CardDescription>Notification email service</CardDescription>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-700 rounded-sm">Active</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Email notifications are powered by Resend. Use the Notifications tab to configure recipients and test the connection.
                </p>
              </CardContent>
            </Card>

            {/* Terranotes Card - Placeholder */}
            <Card className="rounded-sm border opacity-60">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center">
                      <span className="text-blue-700 font-bold text-lg">TN</span>
                    </div>
                    <div>
                      <CardTitle className="text-lg">Terranotes</CardTitle>
                      <CardDescription>Import property data</CardDescription>
                    </div>
                  </div>
                  <Badge variant="outline" className="rounded-sm">Coming Soon</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <Button disabled className="w-full rounded-sm">
                  <Link2 className="w-4 h-4 mr-2" /> Connect Terranotes
                </Button>
              </CardContent>
            </Card>
            </div>
          </div>
        </TabsContent>

        {/* Data Import Tab */}
        <TabsContent value="import">
          <div className="space-y-6">
            {/* Import Instructions */}
            <Card className="rounded-sm border bg-blue-50">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-800">Transaction Import</p>
                    <p className="mt-1 text-blue-700">
                      To import transactions from QuickBooks Online, go to the specific project and use the 
                      <strong> &quot;Import from QBO&quot;</strong> button in the Transactions tab.
                    </p>
                    <p className="mt-2 text-blue-700">
                      This allows you to export a Transaction List by Date report from QBO and import it directly to the project.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* How to Export from QBO */}
            <Card className="rounded-sm border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-forest-500" />
                  How to Export from QuickBooks Online
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>In QBO, go to <strong>Reports → Transaction List by Date</strong></li>
                  <li>Set your date range and filter by the project/property if needed</li>
                  <li>Click <strong>Export</strong> and choose <strong>Export to Excel</strong></li>
                  <li>Open the Excel file and save as CSV</li>
                  <li>In LandFlow, go to the project and click <strong>Import from QBO</strong></li>
                </ol>
                <div className="mt-4 p-3 bg-muted/30 rounded-sm">
                  <p className="text-xs font-medium">Supported QBO Columns:</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Date, Transaction Type, Num, Name, Memo/Description, Amount, Account, Category
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Bulk Property Import */}
            <Card className="rounded-sm border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Upload className="w-5 h-5 text-forest-500" />
                  Bulk Property Import
                </CardTitle>
                <CardDescription>Import multiple properties from a CSV file</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-muted/30 rounded-sm">
                  <p className="text-sm font-medium mb-2">CSV Format Requirements:</p>
                  <p className="text-xs text-muted-foreground">
                    Your CSV should include headers for: <code className="bg-muted px-1 rounded">name</code>, <code className="bg-muted px-1 rounded">status</code>, <code className="bg-muted px-1 rounded">strategy</code>, <code className="bg-muted px-1 rounded">purchase_price</code>, <code className="bg-muted px-1 rounded">acres</code>, <code className="bg-muted px-1 rounded">county</code>, <code className="bg-muted px-1 rounded">state</code>
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Optional: <code className="bg-muted px-1 rounded">sale_price</code>, <code className="bg-muted px-1 rounded">acquisition_date</code>, <code className="bg-muted px-1 rounded">sale_date</code>, <code className="bg-muted px-1 rounded">projected_profit</code>, <code className="bg-muted px-1 rounded">notes</code>
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="file"
                    id="bulk-import-file"
                    accept=".csv"
                    onChange={(e) => handleFileSelect(e, 'projects')}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('bulk-import-file').click()}
                    disabled={importing}
                    className="rounded-sm"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Select CSV File
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const csvContent = "name,status,strategy,purchase_price,acres,county,state,notes\nSample Property,Lead,Flip,50000,10,Sample County,TX,Sample notes";
                      const blob = new Blob([csvContent], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'property_import_template.csv';
                      a.click();
                      window.URL.revokeObjectURL(url);
                    }}
                    className="rounded-sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                </div>
                
                {importResults && importType === 'projects' && (
                  <div className={`p-4 rounded-sm border ${importResults.errors?.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                    <p className="font-medium text-sm">
                      {importResults.errors?.length > 0 ? 'Import completed with warnings' : 'Import successful!'}
                    </p>
                    <p className="text-sm mt-1">
                      {importResults.success_count} properties imported successfully
                    </p>
                    {importResults.errors?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-amber-700">Errors:</p>
                        <ul className="text-xs text-amber-600 mt-1">
                          {importResults.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>• {err}</li>
                          ))}
                          {importResults.errors.length > 5 && (
                            <li>...and {importResults.errors.length - 5} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>


    </div>
  );
};
